/**
 * Thin HTTP client for FlowRide auth endpoints (`/api/auth/*`).
 */

import axios from "axios";
import { getDeviceMetadata } from "./device-metadata.helpers";

/** Login / refresh response body. */
export interface AuthTokenResponse {
    "access_token": string;
    "token_type":   "Bearer";
    "expires_in":   number;
    "user": {
        "id":        string;
        "firstname": string;
        "lastname":  string;
        "mail":      string;
        "birthdate": string | null;
    };
}

/** Axios instance dedicated to auth cookie + bearer flows. */
const authAxios = axios.create( {
    "withCredentials": true,
    "timeout":         30_000,
} );

/** In-flight refresh promise shared by proactive and reactive callers. */
let refreshInFlight: Promise<AuthTokenResponse> | null = null;

/**
 * Completes SwissPass authorization-code login against the FlowRide backend.
 *
 * @param input - Authorization code and PKCE verifier.
 * @returns Access token response.
 */
export async function loginWithSwissPass( input: {
    "code":         string;
    "codeVerifier": string;
} ): Promise<AuthTokenResponse> {
    const device = getDeviceMetadata();
    const { data } = await authAxios.post<AuthTokenResponse>( "/api/auth/login", {
        "type":          "swisspass",
        "code":          input.code,
        "code_verifier": input.codeVerifier,
        "device_type":   device.device_type,
        "browser":       device.browser,
        "os":            device.os,
    } );

    return data;
}

/**
 * Silently refreshes the FlowRide access token using the HttpOnly refresh cookie.
 * Concurrent callers share a single in-flight request (single-flight).
 *
 * @returns Access token response.
 */
export async function refreshAccessToken(): Promise<AuthTokenResponse> {
    if ( refreshInFlight != null ) {
        return refreshInFlight;
    }

    refreshInFlight = ( async() => {
        try {
            const { data } = await authAxios.post<AuthTokenResponse>( "/api/auth/refresh" );

            return data;
        } finally {
            refreshInFlight = null;
        }
    } )();

    return refreshInFlight;
}

/**
 * Revokes the current session on the backend.
 *
 * @param accessToken - Current Bearer access token.
 * @returns Resolves when logout completes (204).
 */
export async function logoutSession( accessToken: string ): Promise<void> {
    await authAxios.post( "/api/auth/logout", undefined, { "headers": { "Authorization": `Bearer ${ accessToken }` } } );
}
